import { useEffect, useRef, useState } from "react";

const faqs = [
  {
    question: "What types of health insurance plans does HealthMarkets offer?",
    answer:
      "We offer a comprehensive range of private health insurance plans including Individual & Family Plans (ACA/Marketplace), Medicare Advantage, Medicare Supplement (Medigap), Part D Prescription Drug Plans, Dental & Vision Insurance, Short-Term Health Plans, Critical Illness coverage, and Group Health Insurance for small businesses. Our licensed agents in Irving, TX help you compare plans from 50+ top carriers.",
  },
  {
    question: "Is HealthMarkets Insurance Agency open 24 hours a day?",
    answer:
      "Yes! HealthMarkets Insurance Agency is open 24 hours a day, 7 days a week — including weekends and holidays. You can call us anytime at 972-217-4662 or visit our office at 1800 Market Pl Blvd #140, Irving, TX 75063. We understand health insurance questions don't follow a 9-to-5 schedule.",
  },
  {
    question: "Does it cost anything to use HealthMarkets Insurance services?",
    answer:
      "No! Our services are completely free to you. As independent insurance agents, we are compensated by the insurance carriers, not by our clients. You get the same price (or better) than going directly to an insurer, plus you get our expert guidance at zero cost to you.",
  },
  {
    question: "What is the difference between Medicare Advantage and Medicare Supplement?",
    answer:
      "Medicare Advantage (Part C) replaces Original Medicare and is offered by private insurance companies. It often includes extra benefits like dental, vision, and hearing. Medicare Supplement (Medigap) works alongside Original Medicare to help cover out-of-pocket costs like copays and deductibles. Our agents can walk you through both options to find the right fit for your healthcare needs and budget.",
  },
  {
    question: "Can HealthMarkets help me if I missed Open Enrollment?",
    answer:
      "Possibly yes! If you experience a qualifying life event (job loss, marriage, divorce, birth of a child, relocation, etc.) you may qualify for a Special Enrollment Period. Additionally, short-term health plans may be available year-round. Call us at 972-217-4662 and we'll evaluate your eligibility options right away.",
  },
  {
    question: "How do I know which health insurance plan is right for my family?",
    answer:
      "Our licensed agents perform a thorough needs assessment based on your family size, current health conditions, preferred doctors/hospitals, prescription medications, and budget. We then compare plans across 50+ carriers to find the best fit. There's no pressure — we educate and guide, and you decide. Schedule a free consultation at our Irving, TX office or by phone anytime.",
  },
  {
    question: "Does HealthMarkets offer health insurance for small businesses?",
    answer:
      "Yes! We specialize in group health insurance for small businesses of all sizes in the Irving, TX area. We help employers set up group health, dental, vision, life, and disability plans that attract and retain employees. We'll also explain employer contribution strategies and compliance requirements.",
  },
  {
    question: "What documents do I need to enroll in a health insurance plan?",
    answer:
      "Typically you'll need a government-issued ID, Social Security numbers for each enrollee, income information (for ACA plans), current insurance information (if applicable), and a list of current medications and preferred doctors. Our agents will guide you through exactly what's needed for your specific plan type.",
  },
];

export default function FAQSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleScrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="faq" className="py-24 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-['Open_Sans'] font-600 tracking-widest uppercase mb-4">
            FAQ
          </span>
          <h2 className="font-['Poppins'] font-700 text-4xl lg:text-5xl text-emerald-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="font-['Open_Sans'] text-gray-500 max-w-xl mx-auto text-base leading-relaxed">
            Have questions about private health insurance? We've got answers. Can't find what you're looking for? Call us anytime at 972-217-4662.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Sidebar */}
          <div className={`lg:col-span-2 transition-all duration-700 delay-150 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="bg-emerald-900 rounded-lg p-8 text-white">
              <div className="w-14 h-14 rounded-lg bg-white/15 flex items-center justify-center mb-6">
                <i className="ri-question-answer-line text-3xl text-amber-400"></i>
              </div>
              <h3 className="font-['Poppins'] font-700 text-2xl mb-4">Still have questions?</h3>
              <p className="font-['Open_Sans'] text-white/75 text-sm leading-relaxed mb-6">
                Our licensed agents are available around the clock to answer any question about <strong className="text-white">health insurance plans</strong> in Irving, TX.
              </p>
              <a
                href="tel:9722174662"
                className="flex items-center gap-2 px-5 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-white font-['Poppins'] font-600 text-sm transition-colors cursor-pointer whitespace-nowrap mb-4 w-full justify-center"
              >
                <i className="ri-phone-line"></i> 972-217-4662
              </a>
              <button
                onClick={handleScrollToContact}
                className="flex items-center gap-2 px-5 py-3 rounded-lg border-2 border-white/40 hover:border-white text-white font-['Poppins'] font-600 text-sm transition-colors cursor-pointer whitespace-nowrap w-full justify-center"
              >
                <i className="ri-mail-send-line"></i> Send a Message
              </button>
              <div className="mt-8 pt-6 border-t border-white/20">
                <div className="flex items-center gap-2 text-white/70 text-xs font-['Open_Sans']">
                  <i className="ri-time-line text-amber-400"></i>
                  Available 24 hours, 7 days a week
                </div>
                <div className="flex items-center gap-2 text-white/70 text-xs font-['Open_Sans'] mt-2">
                  <i className="ri-map-pin-line text-amber-400"></i>
                  1800 Market Pl Blvd #140, Irving, TX
                </div>
              </div>
            </div>
          </div>

          {/* Accordion */}
          <div
            className={`lg:col-span-3 space-y-3 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
          >
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`border rounded-lg overflow-hidden transition-all duration-200 ${
                  openIndex === i ? "border-emerald-400" : "border-gray-200"
                }`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className={`w-full flex items-center justify-between gap-4 px-6 py-4 text-left transition-colors cursor-pointer ${
                    openIndex === i ? "bg-emerald-50" : "bg-white hover:bg-stone-50"
                  }`}
                >
                  <span
                    className={`font-['Poppins'] font-600 text-sm leading-snug ${
                      openIndex === i ? "text-emerald-900" : "text-gray-800"
                    }`}
                  >
                    {faq.question}
                  </span>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openIndex === i ? "bg-emerald-700" : "bg-gray-100"}`}>
                    <i className={`text-sm transition-all duration-300 ${openIndex === i ? "ri-subtract-line text-white" : "ri-add-line text-gray-600"}`}></i>
                  </div>
                </button>
                {openIndex === i && (
                  <div className="px-6 pb-5 bg-emerald-50 border-t border-emerald-100">
                    <p className="font-['Open_Sans'] text-gray-600 text-sm leading-relaxed pt-4">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
